import React, { type PropsWithChildren, useMemo } from 'react';

import type { SearchQueryStringParams } from '../../../../../common/constants/schemas/query-params';
import { parseQueryParams } from '../../../../../common/utils/query-params';
import type { BootstrapContainerProps } from '../../index';
import { DynamicBootstrapContext } from '../index';

export type DynamicContextProviderProps = {
	// Stricter typing than in BootstrapContainerProps["initialProps"], namely no ? typing, only explicit `| undefined`
	referrer: string | undefined;
	searchSessionId: string;
	smartLinkUsage: Record<string, number> | undefined;
};

export type DynamicBootstrapContextState = {
	queryParams: SearchQueryStringParams;
	referrer: string | undefined;
	searchSessionId: string;
	smartLinkUsage: Record<string, number> | undefined;
};

export const defaultDynamicContext = {
	queryParams: {},
	referrer: undefined,
	/**
	 * Empty because it is falsy and then will be overridden during boostrap
	 * Can't be uuid() because then won't be overridden!
	 * Can't be undefined, because we want to be explicit about the fact that this should be set during bootstrap and not be optional
	 */
	searchSessionId: '',
	smartLinkUsage: undefined,
};

/**
 * In QS-9194 we are using this dynamic provider to seperate values from the bootstrap store migration that are expected to change
 * We do not expect this to be performant, and later in the migration are highly likely to move to prop drilling the values here
 *
 * See: https://product-fabric.atlassian.net/browse/QS-8575
 */
export const DynamicContextProvider = ({
	children,
	searchSessionId,
	referrer,
	smartLinkUsage,
	queryParams,
}: PropsWithChildren<BootstrapContainerProps & DynamicContextProviderProps>): React.JSX.Element => {
	const parsedQueryParams = useMemo(() => {
		return parseQueryParams(queryParams || defaultDynamicContext.queryParams);
	}, [queryParams]);

	// A classic anything-updates useMemo
	const dynamicProps: DynamicBootstrapContextState = useMemo(() => {
		return {
			queryParams: parsedQueryParams,
			// Passed directly through for easy hook access in code sites
			// TODO QS-9194 should all be prop drilled later
			searchSessionId,
			referrer,
			smartLinkUsage,
		};
	}, [parsedQueryParams, referrer, searchSessionId, smartLinkUsage]);

	return (
		<DynamicBootstrapContext.Provider value={dynamicProps}>
			{children}
		</DynamicBootstrapContext.Provider>
	);
};
